import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { adminListPendingBusinesses, adminDecideBusiness, adminGetKvkSignedUrl } from "@/lib/admin.functions";
import { VoysCallButton } from "@/components/voys/VoysCallButton";

export const Route = createFileRoute("/_authenticated/admin/klanten")({
  head: () => ({ meta: [{ title: "Admin — Klantenbeheer" }, { name: "robots", content: "noindex" }] }),
  component: AdminCustomersPage,
});

function AdminCustomersPage() {
  const { isAdmin, loading } = useAuth();
  const list = useServerFn(adminListPendingBusinesses);
  const decide = useServerFn(adminDecideBusiness);
  const sign = useServerFn(adminGetKvkSignedUrl);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-businesses"],
    queryFn: () => list({}),
    enabled: isAdmin,
  });

  const decideMut = useMutation({
    mutationFn: (input: { user_id: string; decision: "approved" | "rejected" }) => decide({ data: input }),
    onSuccess: () => { toast.success("Bijgewerkt"); qc.invalidateQueries({ queryKey: ["admin-businesses"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const openDoc = async (path: string) => {
    try {
      const { url } = await sign({ data: { path } });
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kon document niet openen");
    }
  };

  if (loading) return <p>Laden…</p>;
  if (!isAdmin) return <p className="text-[#3a7bd5]">Geen toegang.</p>;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Zakelijke klanten</h1>
      {isLoading && <p>Laden…</p>}
      <div className="space-y-3">
        {data?.profiles.map((p: any) => {
          const bd = Array.isArray(p.business_details) ? p.business_details[0] : p.business_details;
          return (
            <div key={p.id} className="rounded-lg border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{bd?.company_name ?? "—"} <span className="text-xs text-muted-foreground">({p.full_name})</span></div>
                  <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>KvK: {bd?.kvk_number}</span>
                    <span>· BTW: {bd?.vat_number ?? "—"}</span>
                    {p.phone ? (
                      <span className="inline-flex items-center gap-1">
                        · Tel:{" "}
                        <a href={`tel:${p.phone}`} className="hover:text-foreground hover:underline">
                          {p.phone}
                        </a>
                        <VoysCallButton phone={p.phone} size="xs" showLabel={false} variant="ghost" />
                      </span>
                    ) : (
                      <span>· Tel: —</span>
                    )}
                  </div>
                  <div className="text-xs mt-1">Status: <span className="font-semibold">{p.verification_status}</span></div>
                </div>
                <div className="flex gap-2">
                  {bd?.kvk_document_path && (
                    <Button variant="outline" size="sm" onClick={() => openDoc(bd.kvk_document_path)}>Uittreksel bekijken</Button>
                  )}
                  {p.verification_status !== "approved" && (
                    <Button size="sm" onClick={() => decideMut.mutate({ user_id: p.id, decision: "approved" })}>Goedkeuren</Button>
                  )}
                  {p.verification_status !== "rejected" && (
                    <Button size="sm" variant="outline" onClick={() => decideMut.mutate({ user_id: p.id, decision: "rejected" })}>Afwijzen</Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {data?.profiles.length === 0 && <p className="text-muted-foreground">Geen zakelijke accounts.</p>}
      </div>
    </div>
  );
}
